import { useState, useRef, useEffect, useCallback } from "react";
import { Camera, X, RefreshCw, Check, FlipHorizontal } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

interface CameraCaptureProps {
  onCapture: (file: File) => void;
  onClose: () => void;
}

export function CameraCapture({ onCapture, onClose }: CameraCaptureProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const streamRef = useRef<MediaStream | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [facingMode, setFacingMode] = useState<"environment" | "user">("environment");
  const [captured, setCaptured] = useState<string | null>(null);
  const [starting, setStarting] = useState(true);
  const [flash, setFlash] = useState(false);

  const stopStream = useCallback(() => {
    if (streamRef.current) {
      streamRef.current.getTracks().forEach(t => t.stop());
      streamRef.current = null;
    }
  }, []);

  const startCamera = useCallback(async (facing: "environment" | "user") => {
    stopStream();
    setError(null);
    setStarting(true);
    setCaptured(null);
    try {
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { facingMode: facing, width: { ideal: 1920 }, height: { ideal: 1080 } },
        audio: false,
      });
      streamRef.current = stream;
      if (videoRef.current) {
        videoRef.current.srcObject = stream;
        await videoRef.current.play();
      }
    } catch (err: unknown) {
      const e = err as { name?: string };
      if (e?.name === "NotAllowedError" || e?.name === "PermissionDeniedError") {
        setError("Camera permission denied. Please allow camera access in your browser settings and try again.");
      } else if (e?.name === "NotFoundError" || e?.name === "DevicesNotFoundError") {
        setError("No camera found on this device.");
      } else if (e?.name === "OverconstrainedError") {
        if (facing === "environment") {
          startCamera("user");
          return;
        }
        setError("Could not access camera with requested settings.");
      } else {
        setError("Could not open camera. Please check browser permissions.");
      }
    } finally {
      setStarting(false);
    }
  }, [stopStream]);

  useEffect(() => {
    startCamera(facingMode);
    return () => stopStream();
  }, []);

  useEffect(() => {
    const handler = (e: KeyboardEvent) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [onClose]);

  useEffect(() => {
    document.body.style.overflow = "hidden";
    return () => { document.body.style.overflow = ""; };
  }, []);

  const flipCamera = () => {
    const next = facingMode === "environment" ? "user" : "environment";
    setFacingMode(next);
    startCamera(next);
  };

  const capturePhoto = () => {
    const video = videoRef.current;
    const canvas = canvasRef.current;
    if (!video || !canvas) return;

    canvas.width = video.videoWidth || 1280;
    canvas.height = video.videoHeight || 720;
    const ctx = canvas.getContext("2d");
    if (!ctx) return;
    ctx.drawImage(video, 0, 0);
    const dataUrl = canvas.toDataURL("image/jpeg", 0.92);

    setFlash(true);
    setTimeout(() => setFlash(false), 300);
    setTimeout(() => {
      setCaptured(dataUrl);
      stopStream();
    }, 150);
  };

  const retake = () => {
    setCaptured(null);
    startCamera(facingMode);
  };

  const confirmCapture = () => {
    if (!captured || !canvasRef.current) return;
    canvasRef.current.toBlob(blob => {
      if (!blob) return;
      const file = new File([blob], `capture-${Date.now()}.jpg`, { type: "image/jpeg" });
      onCapture(file);
      onClose();
    }, "image/jpeg", 0.92);
  };

  return (
    <div className="fixed inset-0 z-[100] bg-black flex flex-col">

      {/* Header — changes based on state */}
      <div className="flex items-center justify-between p-3 shrink-0 bg-black/80 border-b border-white/10">
        {captured ? (
          <div className="flex items-center gap-2 font-mono text-sm tracking-widest text-emerald-400">
            <Check className="w-4 h-4" />
            PHOTO PREVIEW — LOOKS GOOD?
          </div>
        ) : (
          <div className="flex items-center gap-2 font-mono text-sm tracking-widest text-white/70">
            <Camera className="w-4 h-4" />
            {starting ? "INITIALISING..." : "LIVE CAMERA"}
          </div>
        )}
        <button
          onClick={onClose}
          className="p-2 rounded-full bg-white/10 hover:bg-white/20 text-white transition-colors"
          title="Close (Esc)"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Main area */}
      <div className="flex-1 relative overflow-hidden bg-black flex items-center justify-center">

        {/* Flash overlay */}
        <div
          className={cn(
            "absolute inset-0 bg-white z-20 pointer-events-none transition-opacity duration-300",
            flash ? "opacity-80" : "opacity-0"
          )}
        />

        {error ? (
          <div className="text-center px-8 space-y-4 max-w-sm">
            <Camera className="w-10 h-10 text-white/30 mx-auto" />
            <p className="text-white/70 text-sm leading-relaxed">{error}</p>
            <Button variant="outline" onClick={() => startCamera(facingMode)}
              className="border-white/20 text-white hover:bg-white/10">
              Try Again
            </Button>
          </div>
        ) : captured ? (
          /* Preview state — prominent green-framed image */
          <div className="w-full h-full flex flex-col items-center justify-center p-4 gap-4">
            <div className="relative rounded-xl overflow-hidden border-4 border-emerald-500 shadow-[0_0_40px_rgba(16,185,129,0.4)] max-w-full max-h-[calc(100%-2rem)]">
              <img
                src={captured}
                alt="Captured photo preview"
                className="block max-w-full max-h-[70vh] object-contain"
              />
              <div className="absolute top-3 left-3 bg-emerald-500 text-white text-xs font-mono px-2 py-1 rounded-full flex items-center gap-1">
                <Check className="w-3 h-3" />
                CAPTURED
              </div>
            </div>
            <p className="text-white/50 text-xs font-mono text-center">
              Happy with this? Tap USE THIS PHOTO — or RETAKE to try again.
            </p>
          </div>
        ) : (
          /* Live viewfinder */
          <>
            {starting && (
              <div className="absolute inset-0 flex items-center justify-center z-10 bg-black">
                <span className="text-white/40 font-mono text-xs animate-pulse tracking-widest">
                  STARTING CAMERA...
                </span>
              </div>
            )}
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="max-w-full max-h-full object-contain"
            />
            {/* Corner guides */}
            {!starting && (
              <div className="absolute inset-6 pointer-events-none">
                <div className="absolute top-0 left-0 w-8 h-8 border-t-2 border-l-2 border-white/40 rounded-tl" />
                <div className="absolute top-0 right-0 w-8 h-8 border-t-2 border-r-2 border-white/40 rounded-tr" />
                <div className="absolute bottom-0 left-0 w-8 h-8 border-b-2 border-l-2 border-white/40 rounded-bl" />
                <div className="absolute bottom-0 right-0 w-8 h-8 border-b-2 border-r-2 border-white/40 rounded-br" />
              </div>
            )}
          </>
        )}

        <canvas ref={canvasRef} className="hidden" />
      </div>

      {/* Bottom bar */}
      <div className="shrink-0 bg-black/80 border-t border-white/10 p-5 flex items-center justify-between gap-4">
        {captured ? (
          <>
            <Button
              variant="outline"
              onClick={retake}
              className="border-white/20 text-white hover:bg-white/10 font-mono text-xs gap-2"
            >
              <RefreshCw className="w-4 h-4" />
              RETAKE
            </Button>
            <Button
              onClick={confirmCapture}
              className="flex-1 bg-emerald-600 hover:bg-emerald-500 text-white font-mono tracking-wider gap-2"
            >
              <Check className="w-4 h-4" />
              USE THIS PHOTO
            </Button>
          </>
        ) : (
          <>
            <Button
              variant="outline"
              onClick={flipCamera}
              disabled={starting || !!error}
              className="border-white/20 text-white hover:bg-white/10 font-mono text-xs gap-2"
              title="Flip camera"
            >
              <FlipHorizontal className="w-4 h-4" />
              FLIP
            </Button>

            {/* Shutter button */}
            <button
              onClick={capturePhoto}
              disabled={starting || !!error}
              className="w-18 h-18 relative mx-auto disabled:opacity-30 disabled:cursor-not-allowed group"
              title="Take photo"
              style={{ width: 72, height: 72 }}
            >
              <div className="absolute inset-0 rounded-full border-4 border-white/50 group-hover:border-white/80 transition-colors" />
              <div className="absolute inset-2 rounded-full bg-white group-hover:bg-white/90 group-active:scale-90 transition-all" />
            </button>

            <div className="w-[80px] text-center">
              <span className="text-white/30 font-mono text-xs">TAP TO{"\n"}CAPTURE</span>
            </div>
          </>
        )}
      </div>
    </div>
  );
}
